// Theme store — STRICTLY light or dark, never "system".
//
// The app's appearance must NOT follow the device's
// `prefers-color-scheme`. Samsung Internet, Chrome Android force-dark,
// and several Chromium derivatives aggressively force-invert PWAs
// when the OS is in dark mode and would override our chosen theme.
//
// Behaviour:
//   * Default = "light" (changed in Iter73 — was "dark")
//   * Persisted in localStorage as `rr_theme`
//   * Applied to <html> via ALL of:
//       - `data-theme="dark|light"`  (existing selector contract)
//       - `class="theme-dark|theme-light"`  (Iter73 — added so CSS
//          and external integrations can use class selectors)
//       - inline `style.colorScheme`  (browser native form controls)
//       - `<meta name="color-scheme">`  (PWA shell + status bars)
//   * Legacy values like "system" are coerced to the default on first read
//
// A tiny inline script in `public/index.html` applies the same
// resolution BEFORE React mounts so first paint is never wrong.
import { useEffect, useState } from "react";

const KEY = "rr_theme";
const SUPPORTED = ["light", "dark"];
const DEFAULT = "light";

const listeners = new Set();

const read = () => {
  try {
    const v = localStorage.getItem(KEY);
    return SUPPORTED.includes(v) ? v : DEFAULT;
  } catch (_e) { return DEFAULT; }
};

let cur = read();

// Kept for backwards-compat with call sites that import `resolveTheme`;
// it's now an identity function since "system" no longer exists.
export const resolveTheme = (pref) => (SUPPORTED.includes(pref) ? pref : DEFAULT);

const apply = () => {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  // 1) data-theme attribute (existing Tailwind selector contract).
  html.setAttribute("data-theme", cur);
  // 2) theme-{light,dark} class — Iter73 documented contract. Strip
  //    the opposite class first so toggling cleanly removes the old
  //    state without leaving both classes on the element.
  html.classList.remove("theme-light", "theme-dark");
  html.classList.add(cur === "dark" ? "theme-dark" : "theme-light");
  // 3) inline style.colorScheme — tells the browser which native
  //    form-control palette to use (scrollbars, inputs, checkboxes).
  html.style.colorScheme = cur;
  // 4) <meta name="color-scheme"> — sync to the chosen scheme ONLY,
  //    not "light dark". With "light dark" some Chromium / Samsung
  //    builds still apply system-driven force-dark heuristics on the
  //    PWA shell (address bar, status bar). Pinning to one scheme
  //    eliminates that ambiguity.
  let meta = document.querySelector('meta[name="color-scheme"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "color-scheme");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", cur);
};

// Apply immediately so first paint matches user preference.
apply();

export const theme = {
  get() { return cur; },
  set(v) {
    cur = SUPPORTED.includes(v) ? v : DEFAULT;
    try { localStorage.setItem(KEY, cur); } catch (e) { /* noop */ void e; }
    apply();
    listeners.forEach((fn) => { try { fn(cur); } catch (e) { /* noop */ void e; } });
  },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
};

export const useTheme = () => {
  const [v, setV] = useState(cur);
  useEffect(() => theme.subscribe(setV), []);
  return [v, theme.set.bind(theme)];
};
